
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DrawingStorageDashboardRequest struct
 */
class DrawingStorageDashboardRequest {
  /**
   * Initialize struct
   * @param {string=} name
   * @param {number=} version
   * @param {string=} settings
   * @constructor
   */
  constructor (name = null, version = null, settings = null) {
    this.name = name
    this.version = version
    this.settings = settings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DrawingStorageDashboardRequest}
   * @param {!DrawingStorageDashboardRequest} other Other struct
   * @returns {!DrawingStorageDashboardRequest} This struct
   */
  copy (other) {
    if (other.name != null) {
      this.name = other.name
    } else {
      this.name = null
    }
    if (other.version != null) {
      this.version = UInt64.fromNumber(other.version)
    } else {
      this.version = null
    }
    if (other.settings != null) {
      this.settings = other.settings
    } else {
      this.settings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DrawingStorageDashboardRequest}
   * @returns {!DrawingStorageDashboardRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DrawingStorageDashboardRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DrawingStorageDashboardRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DrawingStorageDashboardRequest}
   * @param {!DrawingStorageDashboardRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DrawingStorageDashboardRequest)) {
      throw new TypeError('Instance of DrawingStorageDashboardRequest is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DrawingStorageDashboardRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      name: ((this.name != null) ? this.name : null),
      version: ((this.version != null) ? this.version.toNumber() : null),
      settings: ((this.settings != null) ? this.settings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DrawingStorageDashboardRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DrawingStorageDashboardRequest} other Object value
   * @returns {!DrawingStorageDashboardRequest} Created struct
   */
  static fromObject (other) {
    return new DrawingStorageDashboardRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DrawingStorageDashboardRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DrawingStorageDashboardRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DrawingStorageDashboardRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10005
  }
}

export { DrawingStorageDashboardRequest };

/**
 * Fast Binary Encoding DrawingStorageDashboardRequest field model
 */
class FieldModelDrawingStorageDashboardRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.name = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, 4 + 4), buffer, 4 + 4)
    this.version = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.name.fbeOffset + this.name.fbeSize), buffer, this.name.fbeOffset + this.name.fbeSize)
    this.settings = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.version.fbeOffset + this.version.fbeSize), buffer, this.version.fbeOffset + this.version.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.name.fbeSize + this.version.fbeSize + this.settings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.name.fbeExtra + this.version.fbeExtra + this.settings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDrawingStorageDashboardRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10005
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.name.fbeSize

    if ((fbeCurrentSize + this.version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.version.fbeSize

    if ((fbeCurrentSize + this.settings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.settings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.settings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!DrawingStorageDashboardRequest} fbeValue Default value, defaults is new DrawingStorageDashboardRequest()
   * @returns {!DrawingStorageDashboardRequest} DrawingStorageDashboardRequest value
   */
  get (fbeValue = new DrawingStorageDashboardRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!DrawingStorageDashboardRequest} fbeValue DrawingStorageDashboardRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.name.fbeSize) <= fbeStructSize) {
      fbeValue.name = this.name.get()
    } else {
      fbeValue.name = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.name.fbeSize

    if ((fbeCurrentSize + this.version.fbeSize) <= fbeStructSize) {
      fbeValue.version = this.version.get()
    } else {
      fbeValue.version = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.version.fbeSize

    if ((fbeCurrentSize + this.settings.fbeSize) <= fbeStructSize) {
      fbeValue.settings = this.settings.get()
    } else {
      fbeValue.settings = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.settings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {!DrawingStorageDashboardRequest} fbeValue DrawingStorageDashboardRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDrawingStorageDashboardRequest}
   * @param {DrawingStorageDashboardRequest} fbeValue DrawingStorageDashboardRequest value
   */
  setFields (fbeValue) {
    this.name.set(fbeValue.name)
    this.version.set(fbeValue.version)
    this.settings.set(fbeValue.settings)
  }
}

export { FieldModelDrawingStorageDashboardRequest };

/**
 * Fast Binary Encoding DrawingStorageDashboardRequest model
 */
class DrawingStorageDashboardRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDrawingStorageDashboardRequest(this.buffer, 4)
  }

  /**
   * Get the DrawingStorageDashboardRequest model
   * @this {!DrawingStorageDashboardRequestModel}
   * @returns {!FieldModelDrawingStorageDashboardRequest} model DrawingStorageDashboardRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DrawingStorageDashboardRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DrawingStorageDashboardRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DrawingStorageDashboardRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DrawingStorageDashboardRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDrawingStorageDashboardRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DrawingStorageDashboardRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DrawingStorageDashboardRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DrawingStorageDashboardRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DrawingStorageDashboardRequestModel}
   * @param {!DrawingStorageDashboardRequest} value DrawingStorageDashboardRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DrawingStorageDashboardRequestModel}
   * @param {!DrawingStorageDashboardRequest} value DrawingStorageDashboardRequest value, defaults is new DrawingStorageDashboardRequest()
   * @return {!object} Deserialized DrawingStorageDashboardRequest value and its size
   */
  deserialize (value = new DrawingStorageDashboardRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DrawingStorageDashboardRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DrawingStorageDashboardRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DrawingStorageDashboardRequestModel}
   * @param {!number} prev Previous DrawingStorageDashboardRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DrawingStorageDashboardRequestModel };
